import { Aviso, Botao } from "../ui";
import { permissoesSensiveis, useCatalogoDePermissoesDosUsuarios } from "../../nucleo/acesso";
import { mensagemDoConsole } from "../../nucleo/console";
import { useFocoDaConfirmacao } from "../../nucleo/foco";
import { rotuloDaPermissao } from "../../nucleo/permissoesDoConsole";
import type { MatrizDeTelas, PessoaComTelas } from "../../nucleo/telas";

/**
 * Antes de trocar o perfil de alguém da equipe que tem ajuste individual: os ajustes continuam
 * depois da troca (é a regra), e a pergunta diz o que cada um vira diante do perfil novo, com a
 * concessão sensível marcada. Vale para todo caminho que troca o perfil — o seletor da tabela e
 * os botões de perfil pronto —, para que nenhum deles troque sem mostrar isso. Sem o catálogo
 * não há como marcar o que é sensível, então o botão espera por ele.
 */
export function ConfirmacaoDeTrocaDePerfil({
  pessoa,
  papel,
  origem,
  matriz,
  nomeDoPerfil,
  enviando,
  aoCancelar,
  aoConfirmar,
}: {
  pessoa: PessoaComTelas;
  papel: string;
  /** O controle que pediu a troca: o foco volta para ele quando a pergunta fecha. */
  origem: HTMLElement | null;
  matriz: MatrizDeTelas;
  nomeDoPerfil: (chave: string) => string;
  enviando: boolean;
  aoCancelar: () => void;
  aoConfirmar: () => void;
}) {
  const bloco = useFocoDaConfirmacao<HTMLDivElement>(`${pessoa.usuario.id}:${papel}`, origem);
  const catalogo = useCatalogoDePermissoesDosUsuarios();
  const sensiveis = permissoesSensiveis(catalogo.data);
  const novo = nomeDoPerfil(papel);
  const doNovo = new Set(matriz.perfis.find((perfil) => perfil.chave === papel)?.permissoes ?? []);

  function efeitoDoAjuste(permissao: string, efeito: "grant" | "revoke"): string {
    if (efeito === "grant") {
      return doNovo.has(permissao)
        ? `concedida para esta pessoa; o perfil ${novo} também a dá`
        : `continua concedida, embora o perfil ${novo} não a tenha`;
    }
    return doNovo.has(permissao)
      ? `continua retirada, embora o perfil ${novo} a tenha`
      : `retirada desta pessoa; o perfil ${novo} também não a tem`;
  }

  return (
    <Aviso tom="atencao" className="mt-3" data-testid="confirmar-troca-de-perfil">
      <div
        ref={bloco}
        tabIndex={-1}
        role="group"
        aria-label={`Confirmar troca do perfil de ${pessoa.usuario.nome}`}
        onKeyDown={(evento) => {
          if (evento.key === "Escape") aoCancelar();
        }}
      >
        <p>
          Trocar o perfil de {pessoa.usuario.nome} para {novo}? Os ajustes individuais continuam
          valendo depois da troca:
        </p>
        <ul>
          {pessoa.ajustes.map(({ permissao_chave, efeito }) => (
            <li key={permissao_chave}>
              {rotuloDaPermissao(permissao_chave)}: {efeitoDoAjuste(permissao_chave, efeito)}
              {efeito === "grant" && sensiveis.has(permissao_chave) && !doNovo.has(permissao_chave) ? (
                <b> (permissão sensível)</b>
              ) : null}
            </li>
          ))}
        </ul>
        <p className="celula-secundaria">Para tirar um ajuste, use Permissões depois da troca.</p>
        {catalogo.data ? null : catalogo.isError ? (
          <p role="alert">
            Não deu para conferir quais permissões são sensíveis. {mensagemDoConsole(catalogo.error)}{" "}
            <Botao tamanho="pequeno" variante="fantasma" onClick={() => void catalogo.refetch()}>
              Tentar de novo
            </Botao>
          </p>
        ) : (
          <p role="status">Conferindo quais permissões são sensíveis…</p>
        )}
        <Botao tamanho="pequeno" variante="fantasma" onClick={aoCancelar}>Cancelar</Botao>{" "}
        <Botao tamanho="pequeno" disabled={enviando || !catalogo.data} onClick={aoConfirmar}>
          Confirmar troca para {novo}
        </Botao>
      </div>
    </Aviso>
  );
}
